import React, { FC, useCallback, useEffect } from 'react';
import {
  Control,
  useFieldArray,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { CalendarIcon, CloseIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { OverlayDatePicker as DatePicker } from '../../../../components/date-picker/index';
import { FormInput } from '../../../../components/input/index';
import { useFormat } from '../../../../hooks/index';
import { SharePlanType } from '../../../../types/pool-plans.types';
import { FormSchema } from '../exercise-form.validation';
import { GrantCombobox, StakeholderCombobox, TableHeaderItem } from '../form-components/index';

export type StockOptionProps = {
  companyId: string;
  control: Control<FormSchema>;
  formData: UseFormGetValues<FormSchema>;
  resetField: UseFormResetField<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

export const StockOption: FC<StockOptionProps> = ({
  control,
  setFormData,
  companyId,
  resetField,
  formData,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const { format } = useFormat();

  const {
    fields: stockItemsFields,
    append: stockItemsAppend,
    remove: stockItemsRemove,
  } = useFieldArray({
    control,
    name: 'stepOne.commonStock.stockItems',
  });

  const handleRemoveStockItems = useCallback(
    (index: number) => {
      stockItemsRemove(index);
    },
    [stockItemsRemove],
  );

  const handleAppendStockItems = useCallback(() => {
    const defaultDate = new Date();
    stockItemsAppend({
      stakeholder: { id: '', fullName: '' },
      grant: {
        id: '',
        grantItem: {
          plan: { strikePrice: 0, conversionRatio: 1, pool: { shareClass: { name: '' } } },
        },
        exercisedCount: 0,
        vestedSharesCount: 0,
      },
      date: defaultDate,
      exercised: 0,
    });
  }, [stockItemsAppend]);

  const handleStakeholderChange = useCallback(
    (index: number) => {
      resetField(`stepOne.commonStock.stockItems.${index}.grant`);
    },
    [resetField],
  );

  const calculateBalance = useCallback(
    (index: number) => {
      const vestedShares = stepOne?.commonStock?.stockItems?.[index]?.grant?.vestedSharesCount || 0;
      const exercisedShares = stepOne?.commonStock?.stockItems?.[index]?.grant?.exercisedCount || 0;
      const value = stepOne?.commonStock?.stockItems?.[index]?.exercised || 0;
      const balance = Math.max(vestedShares - exercisedShares - value, 0);

      return balance.toLocaleString('en-US');
    },
    [stepOne?.commonStock?.stockItems],
  );

  useEffect(() => {
    if (stockItemsFields.length === 0) {
      handleAppendStockItems();
    }
  }, [handleAppendStockItems, stockItemsFields.length]);

  const showRemoveButton = stockItemsFields.length > 1;

  return (
    <>
      <div className="flex w-[1340px] flex-col rounded-lg border-[1px] border-gray-100 bg-white">
        <div
          className={twMerge(
            'grid',
            showRemoveButton
              ? 'grid-cols-[repeat(3,184px)_repeat(4,minmax(164px,1fr))_repeat(1,48px)]'
              : 'grid-cols-[repeat(3,184px)_repeat(4,minmax(164px,1fr))]',
          )}
        >
          <TableHeaderItem showTooltip text={<AppFormattedMessage id="STAKEHOLDER" />} />
          <TableHeaderItem showTooltip text={<AppFormattedMessage id="GRANT" />} />
          <TableHeaderItem showTooltip text={<AppFormattedMessage id="EXERCISE_DATE" />} />
          <TableHeaderItem text={<AppFormattedMessage id="SHARE_CLASS" />} />
          <TableHeaderItem text={<AppFormattedMessage id="STRIKE_PRICE" />} />
          <TableHeaderItem text={<AppFormattedMessage id="EXERCISED_OPTIONS" />} variant="dark" />
          <TableHeaderItem text={<AppFormattedMessage id="ISSUED_SHARES" />} variant="dark" />
          {showRemoveButton && <TableHeaderItem text="" />}
        </div>
        {stockItemsFields.map((field, index) => (
          <div
            className={twMerge(
              'grid border-b-[1px] border-b-gray-100',
              showRemoveButton
                ? 'grid-cols-[repeat(3,184px)_repeat(4,minmax(164px,1fr))_repeat(1,48px)]'
                : 'grid-cols-[repeat(3,184px)_repeat(4,minmax(164px,1fr))]',
            )}
            key={field.id}
          >
            <div className="px-4 pb-10 pt-7">
              <StakeholderCombobox
                companyId={companyId}
                control={control}
                formData={formData}
                handleStakeholderChange={() => handleStakeholderChange(index)}
                name={`stepOne.commonStock.stockItems.${index}.stakeholder`}
                planType={SharePlanType.STOCK_OPTIONS}
                setFormData={setFormData}
              />
            </div>
            <div className="px-4 pb-10 pt-7">
              <GrantCombobox
                companyId={companyId}
                control={control}
                name={`stepOne.commonStock.stockItems.${index}.grant`}
                selectedGrantIds={stepOne?.commonStock?.stockItems?.map(
                  (item) => item?.grant?.id || '',
                )}
                sharePlanType={SharePlanType.STOCK_OPTIONS}
                stakeholderId={stepOne?.commonStock?.stockItems?.[index]?.stakeholder?.id || ''}
              />
            </div>
            <div className="px-4 pb-10 pt-7">
              <DatePicker
                onSelect={(date) =>
                  setFormData(`stepOne.commonStock.stockItems.${index}.date`, date)
                }
                value={stepOne?.commonStock?.stockItems?.[index]?.date}
                wrapperClassName="w-full"
              >
                <FormInput
                  autoComplete="off"
                  control={control}
                  disabledInput
                  icon={<CalendarIcon className="size-5" iconColor="#344054" />}
                  inputClassName="h-10"
                  name={`stepOne.commonStock.stockItems.${index}.date`}
                  transform={(value) => {
                    return format(value, 'dd/MM/yyyy');
                  }}
                  wrapperClassName="w-full"
                />
              </DatePicker>
            </div>
            <div className="flex items-center px-4 pb-10 pt-7">
              <span className="text-sm font-[450] text-gray-700">
                {
                  stepOne?.commonStock?.stockItems?.[index]?.grant?.grantItem?.plan?.pool
                    ?.shareClass?.name
                }
              </span>
            </div>
            <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
              <FormInput
                control={control}
                disabled={true}
                inputClassName="h-10"
                name={`stepOne.commonStock.stockItems.${index}.grant.grantItem.plan.strikePrice`}
                shouldFormatNumber
                wrapperClassName="w-full"
              />
              <span className="block max-w-[164px] overflow-hidden truncate text-ellipsis whitespace-nowrap text-xs font-[450] text-gray-600">
                <AppFormattedMessage id="TOTAL_COST" /> $
                {(
                  (stepOne?.commonStock?.stockItems?.[index]?.grant?.grantItem?.plan?.strikePrice ||
                    0) * (stepOne?.commonStock?.stockItems?.[index]?.exercised || 1)
                ).toLocaleString('en-US')}
              </span>
            </div>
            <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
              <FormInput
                control={control}
                inputClassName="h-10"
                name={`stepOne.commonStock.stockItems.${index}.exercised`}
                numberOnly
                shouldFormatNumber
                wrapperClassName="w-full"
              />
              <span className="text-xs font-[450] text-gray-600">
                <AppFormattedMessage id="AVAILABLE" /> {calculateBalance(index)}
              </span>
            </div>
            <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
              <div className="max-w-[164px] rounded-t-[4px] bg-gray-50 px-3 py-2">
                <span className="block w-full overflow-hidden truncate text-ellipsis whitespace-nowrap text-sm font-[450] text-gray-700">
                  {(
                    (stepOne?.commonStock?.stockItems?.[index]?.exercised || 0) *
                    (stepOne?.commonStock?.stockItems?.[index]?.grant?.grantItem?.plan
                      ?.conversionRatio || 1)
                  ).toLocaleString('en-US')}
                </span>
              </div>
              <span className="text-xs font-[450] text-gray-600">
                <AppFormattedMessage id="CONVERSION" />{' '}
                {stepOne?.commonStock?.stockItems?.[index]?.grant?.grantItem?.plan
                  ?.conversionRatio || 1}
                x
              </span>
            </div>
            {showRemoveButton && (
              <div>
                <Button
                  className="flex h-full w-full items-center justify-center"
                  onClick={() => handleRemoveStockItems(index)}
                  styleType="NONE"
                >
                  <CloseIcon />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {stockItemsFields.length < 10 && (
        <div className="sticky bottom-[76px] z-10 w-full bg-white">
          <Button
            className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent"
            onClick={handleAppendStockItems}
            styleType="NONE"
          >
            + <AppFormattedMessage id="ADD_EXERCISING" />
          </Button>
        </div>
      )}
    </>
  );
};
